import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Select,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute,Link } from '@tanstack/react-router';
import { useState } from 'react';

import { AdminActionModal } from '../../../components/admin-shell/AdminActionModal';
import { AdminShell } from '../../../components/admin-shell/AdminShell';
import { adminGet, adminPost } from '../_api';

type DisputeDetail = {
  dispute: {
    id: string;
    short_id: string;
    exchange_id: string;
    opened_by_user_id: string;
    state: 'opened' | 'acknowledged' | 'under_review' | 'resolved' | 'closed';
    category: string;
    description: string;
    evidence_urls: string[] | null;
    resolution_outcome: string | null;
    resolution_summary: string | null;
    opened_at: string;
    acknowledged_at: string | null;
    resolved_at: string | null;
    closed_at: string | null;
  };
  exchange: {
    id: string;
    state: string;
    artist_user_id: string;
    host_user_id: string;
    venue_name: string;
    artist_name: string;
    host_name: string;
  };
  messages: Array<{
    id: string;
    author_user_id: string;
    audience: 'parties' | 'team_only';
    body: string;
    created_at: string;
  }>;
};

const RESOLUTION_OUTCOMES = [
  { value: 'warning', label: 'Warning' },
  { value: 'account_suspended', label: 'Account suspended' },
  { value: 'account_closed', label: 'Account closed' },
  { value: 'venue_paused', label: 'Venue paused' },
  { value: 'mediated_no_action', label: 'Mediated, no action' },
  { value: 'not_actionable', label: 'Not actionable' },
];

export const Route = createFileRoute('/admin/disputes/$id')({
  component: AdminDisputeDetail,
});

function AdminDisputeDetail() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'disputes', id],
    queryFn: () => adminGet<DisputeDetail>(`/api/admin/disputes/${id}`),
  });

  const [resolveOpen, resolveCtl] = useDisclosure(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  const [summary, setSummary] = useState('');

  const acknowledge = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/disputes/${id}/acknowledge`, { dispute_id: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'disputes', id] }),
  });
  const beginReview = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/disputes/${id}/begin-review`, { dispute_id: id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'disputes', id] }),
  });
  const resolve = useMutation({
    mutationFn: () =>
      adminPost(`/api/admin/disputes/${id}/resolve`, {
        dispute_id: id,
        resolution_outcome: outcome,
        resolution_summary: summary,
      }),
    onSuccess: () => {
      resolveCtl.close();
      setOutcome(null);
      setSummary('');
      void qc.invalidateQueries({ queryKey: ['admin', 'disputes', id] });
    },
  });

  if (isLoading) {
    return (
      <AdminShell breadcrumb="Disputes">
        <Loader size="sm" />
      </AdminShell>
    );
  }
  if (error || !data) {
    return (
      <AdminShell breadcrumb="Disputes">
        <Alert color="red">Could not load this dispute.</Alert>
      </AdminShell>
    );
  }

  const d = data.dispute;
  const ex = data.exchange;

  return (
    <AdminShell breadcrumb={`Disputes / ${d.short_id}`}>
      <Stack gap="lg">
        <Anchor component={Link} to="/admin/disputes" size="sm">
          ← Queue
        </Anchor>

        <Group justify="space-between" align="flex-end">
          <Stack gap={4}>
            <Title order={2}>Dispute {d.short_id}</Title>
            <Group gap="xs">
              <Badge variant="light">{d.category}</Badge>
              <Badge variant="light" color={stateColor(d.state)}>
                {d.state}
              </Badge>
            </Group>
          </Stack>
          <Group>
            {d.state === 'opened' ? (
              <Button onClick={() => acknowledge.mutate()} loading={acknowledge.isPending}>
                Acknowledge
              </Button>
            ) : null}
            {d.state === 'acknowledged' ? (
              <Button onClick={() => beginReview.mutate()} loading={beginReview.isPending}>
                Begin review
              </Button>
            ) : null}
            {(d.state === 'acknowledged' || d.state === 'under_review') ? (
              <Button color="tide" onClick={resolveCtl.open}>
                Resolve
              </Button>
            ) : null}
          </Group>
        </Group>

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Exchange
          </Title>
          <Stack gap={2}>
            <KV k="Venue" v={ex.venue_name} />
            <KV k="Artist" v={ex.artist_name} />
            <KV k="Host" v={ex.host_name} />
            <KV k="Exchange state" v={ex.state} />
          </Stack>
        </Card>

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            What was raised
          </Title>
          <Stack gap={6}>
            <Text style={{ whiteSpace: 'pre-wrap' }}>{d.description}</Text>
            {d.evidence_urls && d.evidence_urls.length > 0 ? (
              <Stack gap={4} mt="xs">
                <Text size="sm" c="dimmed">
                  Evidence
                </Text>
                {d.evidence_urls.map((url) => (
                  <Anchor key={url} href={url} target="_blank" rel="noopener">
                    {url}
                  </Anchor>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </Card>

        {d.resolution_summary ? (
          <Card withBorder padding="md">
            <Title order={5} mb="xs">
              Outcome
            </Title>
            <Stack gap={2}>
              <KV k="Outcome" v={d.resolution_outcome ?? '–'} />
              <KV k="Summary" v={d.resolution_summary} />
              {d.resolved_at ? <KV k="Resolved" v={formatDate(d.resolved_at)} /> : null}
            </Stack>
          </Card>
        ) : null}

        <Card withBorder padding="md">
          <Title order={5} mb="xs">
            Conversation
          </Title>
          <Stack gap="sm">
            {data.messages.length === 0 ? (
              <Text c="dimmed">No messages.</Text>
            ) : (
              data.messages.map((m) => (
                <Stack key={m.id} gap={2}>
                  <Group gap="xs">
                    <Text size="xs" c="dimmed">
                      {formatDate(m.created_at)}
                    </Text>
                    {m.audience === 'team_only' ? (
                      <Badge size="xs" color="tide" variant="light">
                        team-only
                      </Badge>
                    ) : null}
                  </Group>
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                    {m.body}
                  </Text>
                </Stack>
              ))
            )}
          </Stack>
        </Card>
      </Stack>

      <AdminActionModal
        opened={resolveOpen}
        onClose={resolveCtl.close}
        title="Resolve dispute"
        onConfirm={() => resolve.mutate()}
        confirmLabel="Resolve"
        confirmColor="tide"
        loading={resolve.isPending}
        confirmDisabled={outcome === null || summary.trim().length < 20}
      >
        <Select
          label="Outcome"
          data={RESOLUTION_OUTCOMES}
          value={outcome}
          onChange={setOutcome}
          placeholder="Pick an outcome"
        />
        <Textarea
          label="Summary (shown to both parties)"
          description="At least 20 characters. Plain language."
          value={summary}
          onChange={(e) => setSummary(e.currentTarget.value)}
          minRows={4}
        />
      </AdminActionModal>
    </AdminShell>
  );
}

function KV({ k, v }: { k: string; v: string }) {
  return (
    <Group gap="xs" align="flex-start">
      <Text c="dimmed" size="sm" style={{ minWidth: 140 }}>
        {k}
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {v}
      </Text>
    </Group>
  );
}

function stateColor(state: string): string {
  if (state === 'opened') return 'yellow';
  if (state === 'acknowledged' || state === 'under_review') return 'blue';
  if (state === 'resolved') return 'tide';
  return 'gray';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
